import { sameListedWord, wordsOf, type Word } from "../resolvers/vi-words.js";

/**
 * 🔴 AI2D-CONFIRM-QUESTION-ROUTE (@UC0457 · nghiệm thu máy ảo 17/10 E2/E5) — câu HỎI về trạng thái XÁC NHẬN / NHẬN tiền ("nhà nào đang chờ chị xác
 * nhận tiền", "tiền của bé Gia Bảo đã được xác nhận chưa", "ai đã báo chuyển", "còn ai chờ duyệt không"). Đây là câu ĐỌC: Người làm đưa nó vào làn
 * đọc thay vì mở phiếu GHI `PAY-CONFIRM-DECIDE` (E2: chủ nhóm bị hỏi «Lời báo của ai?» + phiếu treo) hay câu từ chối theo vai (E5: thành viên).
 *
 * Hàm này CHỈ phân loại câu — không chọn trang, không điền ô, không ghi. `true` ⇒ caller (`readLaneTarget`) thử làn đọc (0 phiếu); làn đọc lùi
 * hay `false` ⇒ đường cũ NGUYÊN VẸN. Vì vậy chiều sai an toàn là `false` (đường phiếu cũ, mọi chốt §3.7 còn nguyên), và hàm cố ý HẸP:
 *
 *  1. CHỦ ĐỀ — câu nhắc một cụm xác nhận/nhận tiền (`TOPIC`): xác nhận · duyệt · đối chiếu · ghi nhận · lời báo · báo (đã) chuyển/đóng · nhận được ·
 *     nhận tiền. So theo TỪ (`sameListedWord`: gõ không dấu khớp; gõ có dấu phải đúng dấu).
 *  2. HÌNH CÂU HỎI — một trong:
 *     (a) từ cuối (bỏ trợ từ/xưng hô đuôi `TAIL`) là chưa · không · ko — câu hỏi có/không ("… đã được xác nhận chưa");
 *     (b) có từ để hỏi `ai` · `nào` · `bao nhiêu` · `mấy`, hoặc dấu "?" — CHỈ khi câu KHÔNG mở đầu bằng động từ việc (`isImperativeHead`):
 *         "duyệt cho những ai đã báo chuyển" là LỆNH (chữ "ai" ở đây là đại từ quan hệ), không phải câu hỏi.
 *  3. KHÔNG có dấu hiệu LỆNH (`COMMAND`: giúp · giùm · dùm · hộ · hãy · làm ơn · "đi" cuối câu) — "xác nhận giúp tiền bé X chưa?" ⇒ `false`.
 *  4. KHÔNG nói việc KHÁC cũng có chữ "duyệt" (`OTHER_TOPIC`: khoản chi · chi tiêu · tham gia · vào nhóm) — câu đó không phải về lời báo tiền.
 *
 * Câu KHẲNG ĐỊNH ("anh nhận tiền của Nam rồi") · câu LỆNH ("duyệt lời báo của Lan") · câu PHỦ ĐỊNH trần ("chưa nhận được tiền của Minh") · nửa
 * chắc ("hình như Minh chuyển rồi") không có hình câu hỏi ⇒ `false` ⇒ đường phiếu cũ, nơi lưới H08(a) + `guardedChoices` (`hasNegationOrDoubt`)
 * đã bảo đảm phủ định / nghi vấn / nửa chắc KHÔNG BAO GIỜ thành "xác nhận". Câu hỏi đi làn đọc thì càng không: làn đọc không ghi gì. THUẦN, 0 LLM.
 */

type Phrase = readonly Word[];
const phrases = (...xs: readonly string[]): readonly Phrase[] => xs.map((x) => wordsOf(x));

const TOPIC = phrases("xác nhận", "duyệt", "đối chiếu", "ghi nhận", "lời báo", "báo chuyển", "báo đã chuyển", "báo đóng", "báo đã đóng", "nhận được", "nhận tiền");
/** Động từ việc đứng đầu câu (sau xưng hô) ⇒ hình câu LỆNH; chỉ câu hỏi có/không (2a) mới đổi được nó thành câu hỏi. */
const ACTION_HEAD = phrases("xác nhận", "duyệt", "đối chiếu", "ghi nhận", "nhận");
const PERSON = phrases("anh", "em", "chị", "mình", "tôi", "tớ", "tui", "bạn", "cô", "chú", "bác", "con", "cháu");
const YES_NO = phrases("chưa", "không", "ko", "hông");
const WH = phrases("ai", "nào", "bao nhiêu", "mấy");
const COMMAND = phrases("giúp", "giùm", "dùm", "hộ", "hãy", "làm ơn");
const OTHER_TOPIC = phrases("khoản chi", "chi tiêu", "tham gia", "vào nhóm");
const TAIL = phrases("vậy", "thế", "nhỉ", "nhể", "ạ", "à", "hả", "ta", "ơi", "nhé", "nha", "với", "đây", "đó", "em", "anh", "chị", "bạn", "ad");
const GO = wordsOf("đi")[0]!;

/** Cụm `p` bắt đầu tại `i` của `words`. */
const runAt = (words: readonly Word[], i: number, p: Phrase): boolean => p.every((w, j) => words[i + j] !== undefined && sameListedWord(words[i + j]!, w));
const hasRun = (words: readonly Word[], list: readonly Phrase[]): boolean => list.some((p) => words.some((_, i) => runAt(words, i, p)));
const isOne = (w: Word | undefined, list: readonly Phrase[]): boolean => w !== undefined && list.some((p) => p.length === 1 && sameListedWord(w, p[0]!));

/** Bỏ trợ từ / xưng hô ở ĐUÔI câu. */
function withoutTail(words: readonly Word[]): readonly Word[] {
  let end = words.length;
  while (end > 0 && isOne(words[end - 1], TAIL)) end -= 1;
  return words.slice(0, end);
}

/** Câu mở đầu (sau các xưng hô) bằng một động từ việc ("xác nhận …", "anh nhận …", "duyệt …"). */
function isImperativeHead(words: readonly Word[]): boolean {
  let i = 0;
  while (isOne(words[i], PERSON)) i += 1;
  return ACTION_HEAD.some((p) => runAt(words, i, p));
}

/** Câu (một lượt gõ chữ) là câu HỎI về trạng thái xác nhận / nhận tiền — luật ở docblock đầu file. */
export function isConfirmStatusQuestion(sentence: string): boolean {
  const words = wordsOf(sentence);
  if (words.length === 0 || !hasRun(words, TOPIC) || hasRun(words, COMMAND) || hasRun(words, OTHER_TOPIC)) return false;
  const core = withoutTail(words);
  const last = core.at(-1);
  if (last !== undefined && sameListedWord(last, GO)) return false;
  if (isOne(last, YES_NO)) return true;
  if (isImperativeHead(words)) return false;
  return sentence.includes("?") || hasRun(words, WH);
}
